import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  PersonOff as PersonOffIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import userService from "../../../services/userService";
import { USER_STATUS_STYLES, USERS_TABLE_HEADERS, getAccountStatusLabel } from "../constants";
import { formatDateTimeParts } from "../helpers/formatDateTimeParts";

const UsersTable = ({
  users,
  searchTerm,
  setSearchTerm,
  showDeactivatedOnly = false,
  onShowDeactivatedOnlyChange,
  tableLoading = false,
  onSelectUser,
  onCreateUser,
}) => {
  const FALLBACK_VALUE = "-";
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [Permissions, setPermissions] = useState([]);

  useEffect(() => {
    const fetchPermits = async () => {
      try {
        const response = await userService.getPermits();
        setPermissions(response.data);
      } catch (error) {
        console.error("Error al obtener permisos:", error);
      }
    };
    fetchPermits();
  }, []);

  // Valores por defecto centralizados
  const DEFAULT_FORM_STATE = {
    name: "",
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    active: true,
    is_owner: true,
    is_seller: false,
    permissions: [],
  };

  const [form, setForm] = useState(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState({});

  const getDisplayValue = (value) => {
    if (value === null || value === undefined) return FALLBACK_VALUE;
    if (typeof value === "string" && !value.trim()) return FALLBACK_VALUE;
    return value;
  };

  const getStatusChipSx = (status) =>
    USER_STATUS_STYLES[status] ?? {
      bgcolor: "grey.200",
      color: "text.secondary",
    };

  const renderDateTime = (value) => {
    const parts = formatDateTimeParts(value);
    if (!parts) return getDisplayValue(value);

    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" fontWeight={500}>
          {parts.date}
        </Typography>
        {parts.time ? (
          <Typography variant="caption" color="text.secondary">
            {parts.time}
          </Typography>
        ) : null}
      </Stack>
    );
  };

  const handleCloseModal = () => {
    if (creating) return;
    setOpenCreateModal(false);
    setErrors({});
    setForm(DEFAULT_FORM_STATE);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.firstname.trim()) nextErrors.firstname = "El nombre es obligatorio";
    if (!form.lastname.trim()) nextErrors.lastname = "El apellido es obligatorio";
    if (!form.email.trim()) nextErrors.email = "El email es obligatorio";
    if (!form.password.trim()) nextErrors.password = "La contraseña es obligatoria";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setCreating(true);
    try {
      const email = form.email.trim();
      const payload = {
        ...form,
        // El email es la identidad: el username es el propio email.
        name: email,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email,
        company: { name: "VORAS", email: "contact@voras.com" },
        permissions: Permissions.map((p) => ({ id: p.id })),
      };
      await onCreateUser?.(payload);
      handleCloseModal();
    } finally {
      setCreating(false);
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "grey.200",
        overflow: "hidden",
      }}
    >
      {/* --- HEADER DE LA TABLA --- */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "grey.200",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ flex: 1, minWidth: 0 }}
        >
          <TextField
            size="small"
            placeholder="Buscar empresa o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 280 } }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={showDeactivatedOnly ? "deactivated" : "active"}
            onChange={(_e, value) => {
              if (!value) return;
              onShowDeactivatedOnlyChange?.(value === "deactivated");
            }}
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 500,
                px: 1.5,
                border: "none",
                "&.Mui-selected": {
                  bgcolor: "background.paper",
                  color: "primary.main",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  "&:hover": { bgcolor: "background.paper" },
                },
              },
            }}
          >
            <ToggleButton value="active">
              <GroupIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Activos
            </ToggleButton>
            <ToggleButton value="deactivated">
              <PersonOffIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Dados de baja
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={() => setOpenCreateModal(true)}
            >
              + Nuevo usuario
            </Button>
        </Stack>
      </Box>

      {/* --- TABLA --- */}
      <Box sx={{ position: "relative", overflowX: "auto" }}>
        {tableLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.72)",
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead" sx={{ bgcolor: "grey.50" }}>
            <Box component="tr">
              {USERS_TABLE_HEADERS.map((header, index) => (
                <Box
                  key={header || `col-${index}`}
                  component="th"
                  sx={{
                    textAlign: "center",
                    px: 3,
                    py: 1.5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                  }}
                >
                  {header}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {users.length ? (
              users.map((user) => {
                const accountStatus = getAccountStatusLabel(user.active);
                return (
                <Box
                  key={user.id}
                  component="tr"
                  sx={{
                    "&:nth-of-type(even)": { bgcolor: "grey.50" },
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                  onClick={() => onSelectUser(user)}
                >
                  <Box component="td" sx={{ px: 3, py: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {getDisplayValue(user.company)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {getDisplayValue(user.email)}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ px: 3, py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {getDisplayValue(user.plan)}
                    </Typography>
                  </Box>
                  <Box component="td" sx={{ px: 3, py: 1.5 }}>
                    <Chip
                      size="small"
                      label={accountStatus}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        ...getStatusChipSx(accountStatus),
                      }}
                    />
                  </Box>
                  <Box component="td" sx={{ px: 3, py: 1.5 }}>
                    {renderDateTime(user.expiryDate)}
                  </Box>
                  <Box component="td" sx={{ px: 3, py: 1.5 }}>
                    {renderDateTime(user.lastPayment)}
                  </Box>
                  <Box component="td" sx={{ px: 3, py: 1.5, textAlign: "right" }}>
                    <IconButton size="small">
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                );
              })
            ) : (
              <Box component="tr">
                <Box component="td" colSpan={USERS_TABLE_HEADERS.length} sx={{ px: 3, py: 4 }}>
                  <Stack alignItems="center" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {showDeactivatedOnly
                        ? "No hay usuarios dados de baja"
                        : "No se encontraron usuarios"}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {searchTerm
                        ? "Probá con otro término de búsqueda"
                        : "Los resultados aparecerán aquí"}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* --- MODAL DE CREACIÓN (Limpio) --- */}
      <Dialog open={openCreateModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo usuario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Nombre"
                value={form.firstname}
                onChange={(e) => setForm((prev) => ({ ...prev, firstname: e.target.value }))}
                error={Boolean(errors.firstname)}
                helperText={errors.firstname}
                required
                fullWidth
              />
              <TextField
                label="Apellido"
                value={form.lastname}
                onChange={(e) => setForm((prev) => ({ ...prev, lastname: e.target.value }))}
                error={Boolean(errors.lastname)}
                helperText={errors.lastname}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              error={Boolean(errors.email)}
              helperText={errors.email}
              required
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
              error={Boolean(errors.password)}
              helperText={errors.password}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} disabled={creating}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            Crear usuario
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UsersTable;